'use client'

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, TextField, Typography, Snackbar, IconButton, InputAdornment } from '@mui/material'
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';
import LoginLayout from './LoginLayout';
import { useTranslation } from '../common/components/LocalizationProvider';
import { snackBarDurationShortMs } from '../common/util/duration';
import { useCatch, useEffectAsync } from '../reactHelper';
import { sessionActions } from '../store';
import BackIcon from '../common/components/BackIcon';

import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import LockIcon from "@mui/icons-material/Lock"
import VpnKeyIcon from "@mui/icons-material/VpnKey"
import { useTheme } from "@mui/material/styles"

const useStyles = makeStyles()((theme) => ({
  options: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    zIndex: 10,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(10px)",
    padding: theme.spacing(4),
    borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    maxWidth: "400px",
    width: "100%",
    position: "relative",
    zIndex: 1,
  },
  background: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: theme.palette.mode === "dark" ? "url(/dark-background.jpg)" : "url(/light-background.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    zIndex: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: theme.spacing(3),
    fontWeight: 500,
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
  },
  input: {
    width: "100%",
    padding: "12px 20px",
    margin: "8px 0",
    boxSizing: "border-box",
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
}));

const RegisterPage = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useTranslation();
  const theme = useTheme()

  const server = useSelector((state) => state.session.server);
  const totpForce = useSelector((state) => state.session.server.attributes.totpForce);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpKey, setTotpKey] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffectAsync(async () => {
    if (totpForce) {
      const response = await fetch('/api/users/totp', { method: 'POST' });
      if (response.ok) {
        setTotpKey(await response.text());
      } else {
        throw Error(await response.text());
      }
    }
  }, [totpForce, setTotpKey]);

  const handleSubmit = useCatch(async (event) => {
    event.preventDefault();
    const response = await fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, totpKey }),
    });
    if (response.ok) {
      setSnackbarOpen(true);
    } else {
      throw Error(await response.text());
    }
  });

  return (
    <LoginLayout>
      <div className={classes.background} />
      <div className={classes.container}>
        <div className={classes.header}>
          {!server.newServer && (
            <IconButton color="primary" onClick={() => navigate("/login")}>
              <BackIcon />
            </IconButton>
          )}
          <Typography className={classes.title} color="primary">
            {t("loginRegister")}
          </Typography>
        </div>

        <TextField
          required
          placeholder={t("sharedName")}
          name="name"
          value={name}
          autoComplete="name"
          autoFocus
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            ),
            className: classes.input,
          }}
          onChange={(e) => setName(e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
            },
            "& .MuiInput-underline:before": {
              borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
            },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
            },
            "& .MuiInputAdornment-root": {
              color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
            },
          }}
        />

        <TextField
          required
          type="email"
          placeholder={t("userEmail")}
          name="email"
          value={email}
          autoComplete="email"
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
            className: classes.input,
          }}
          onChange={(e) => setEmail(e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
            },
            "& .MuiInput-underline:before": {
              borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
            },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
            },
            "& .MuiInputAdornment-root": {
              color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
            },
          }}
        />

        <TextField
          required
          placeholder={t("userPassword")}
          name="password"
          value={password}
          type="password"
          autoComplete="current-password"
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            className: classes.input,
          }}
          onChange={(e) => setPassword(e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
            },
            "& .MuiInput-underline:before": {
              borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
            },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
            },
            "& .MuiInputAdornment-root": {
              color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
            },
          }}
        />

        {totpForce && (
          <TextField
            required
            placeholder={t("loginTotpKey")}
            name="totpKey"
            value={totpKey || ""}
            variant="standard"
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <VpnKeyIcon />
                </InputAdornment>
              ),
              className: classes.input,
            }}
            sx={{
              "& .MuiInputBase-root": {
                color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiInputAdornment-root": {
                color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
              },
            }}
          />
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          type="submit"
          disabled={!name || !password || !(server.newServer || /(.+)@(.+)\.(.{2,})/.test(email))}
          fullWidth
          className={classes.submitButton}
        >
          {t("loginRegister")}
        </Button>
      </div>
      <Snackbar
        open={snackbarOpen}
        onClose={() => {
          dispatch(sessionActions.updateServer({ ...server, newServer: false }))
          navigate("/login")
        }}
        autoHideDuration={snackBarDurationShortMs}
        message={t("loginCreated")}
      />
    </LoginLayout>
  )
}

export default RegisterPage
